import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { LayoutDashboard, Users, Package, CreditCard, Share2, BarChart3, LogOut, Menu, X, Settings, Bell, PieChart, Layout, Settings2, MessageSquare, KeyRound, HelpCircle, Sparkles, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import usePlatformBranding from '@/hooks/usePlatformBranding';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/tenants', icon: Users, label: 'Tenants' },
  { to: '/admin/packages', icon: Package, label: 'Paket SaaS' },
  { to: '/admin/billing', icon: CreditCard, label: 'Billing SaaS' },
  { to: '/admin/billing-settings', icon: Settings, label: 'Kupon & Add-Ons' },
  { to: '/admin/affiliates', icon: Share2, label: 'Affiliate SaaS' },
  { to: '/admin/reports', icon: BarChart3, label: 'Laporan' },
  { to: '/admin/templates', icon: Layout, label: 'Template Toko' },
  { to: '/admin/notifications', icon: Bell, label: 'Notifikasi' },
  { to: '/admin/wa-notifications', icon: MessageSquare, label: 'WA Notifikasi' },
  { to: '/admin/module-adoption', icon: PieChart, label: 'Module Adoption' },
  { to: '/admin/global-settings', icon: Settings2, label: 'Global Settings' },
  { to: '/admin/faqs', icon: HelpCircle, label: 'Kelola FAQ' },
  { to: '/admin/landing-features', icon: Sparkles, label: 'Fitur Showcase' },
  { to: '/admin/testimonials', icon: Star, label: 'Testimoni' },
  { to: '/admin/account', icon: KeyRound, label: 'Akun Saya' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const branding = usePlatformBranding();
  const logoSrc = branding?.logoUrl || '/logo.png';
  const platformName = branding?.name || 'Tokomoe';

  return (
    <div className="flex h-screen bg-slate-50" data-testid="admin-layout">
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 flex flex-col transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-slate-200">
          <img src={logoSrc} alt={platformName} className="h-8 object-contain" />
          <p className="text-xs text-slate-500 mt-1">Super Admin Panel</p>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink key={to} to={to} end={end} onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive ? 'bg-[#2D6CDF]/10 text-[#2D6CDF]' : 'text-slate-600 hover:bg-slate-100'}`}>
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-[#2D6CDF] flex items-center justify-center text-white text-sm font-bold">
              {user?.name?.[0] || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="w-full mt-2 text-slate-500 hover:text-red-600" onClick={logout} data-testid="admin-logout-btn">
            <LogOut size={16} className="mr-2" /> Keluar
          </Button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center px-4 lg:px-6 sticky top-0 z-30">
          <Button variant="ghost" size="icon" className="lg:hidden mr-2" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="admin-menu-toggle">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </Button>
          <h2 className="text-sm font-semibold text-slate-700">Admin Panel</h2>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="max-w-[1600px] mx-auto fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
